/**
 * MathHelpers - small 3D math utilities used throughout Genesys.
 */

import * as THREE from 'three';
export class MathHelpers {
  /**
   * Creates a THREE.Vector3 from forward/right/up components.
   *
   * @param options - Components to include in the vector
   * @param options.forward - Forward component (will be converted to -Z)
   * @param options.right - Right component (X axis)
   * @param options.up - Up component (Y axis)
   * @returns A new THREE.Vector3
   */
  static makeVector(options: {forward?: number, right?: number, up?: number}): THREE.Vector3 {
    return new THREE.Vector3(
      (options.right ?? 0),
      (options.up ?? 0),
      -(options.forward ?? 0)
    );
  }

  /** Creates a zero vector. */
  static makeZeroVector(): THREE.Vector3 {
    return new THREE.Vector3(0, 0, 0);
  }

  /** Creates a THREE.Vector3 from a dictionary. */
  static vectorFromDict(dict: {x?: number, y?: number, z?: number}): THREE.Vector3 {
    return new THREE.Vector3(
      (dict.x ?? 0),
      (dict.y ?? 0),
      (dict.z ?? 0)
    );
  }

  /**
   * Creates a THREE.Euler rotation from yaw/pitch/roll components (in radians).
   *
   * @param options - Components to include in the rotation
   * @param options.yaw - Yaw rotation (will be converted to negative Y rotation)
   * @param options.pitch - Pitch rotation (X axis)
   * @param options.roll - Roll rotation (will be converted to negative Z rotation)
   * @returns A new THREE.Euler
   */
  static makeRotation(options: {yaw?: number, pitch?: number, roll?: number}): THREE.Euler {
    return new THREE.Euler(
      (options.pitch ?? 0),
      -(options.yaw ?? 0),
      -(options.roll ?? 0)
    );
  }

  /**
   * Creates a THREE.Euler rotation from yaw/pitch/roll components (in degrees).
   *
   * @param options - Components to include in the rotation
   * @param options.yaw - Yaw rotation (will be converted to negative Y rotation)
   * @param options.pitch - Pitch rotation (X axis)
   * @param options.roll - Roll rotation (will be converted to negative Z rotation)
   * @returns A new THREE.Euler
   */
  static makeRotationDegrees(options: {yaw?: number, pitch?: number, roll?: number}): THREE.Euler {
    return new THREE.Euler(
      (options.pitch ?? 0) * THREE.MathUtils.DEG2RAD,
      -(options.yaw ?? 0) * THREE.MathUtils.DEG2RAD,
      -(options.roll ?? 0) * THREE.MathUtils.DEG2RAD
    );
  }

}
